/**
 * BatNestSystem — manages bat nest placement, proximity activation,
 * bat spawning from nests, cooldown tracking, and hammer destruction.
 *
 * Responsibilities:
 *   - Place 1-2 bat nests on interior bridge-wall cells at level generation (level 4+)
 *   - Convert the nest wall cell to walkable (PATH) so the player CAN traverse it,
 *     but with a penalty (2× damage + 2× coin loss), handled by GameScene
 *   - Replace the wall sprite with the nest sprite and add crumbled-wall visuals
 *     on adjacent wall cells to show the nest has partially destroyed the wall
 *   - Track player proximity and activate nests within the activation radius
 *   - Spawn BatEntity instances from activated nests (staggered, 3 per burst)
 *   - Manage nest cooldown timers (12s between activations)
 *   - Handle hammer B-key combo hits on nest cells (3 hits to destroy)
 *   - Toggle nest + crumbled wall fog visibility (only within fog reveal radius)
 *   - Serialize/restore for save system
 *   - Clean up on level change / scene shutdown
 *
 * Nests sit on WALL cells (even indices in the expanded grid) that bridge two
 * path cells. The wall cell is converted to PATH so the player can walk through,
 * but traversal carries a penalty. Nests are NOT visible through fog of war —
 * they only appear when the player's fog reveal radius covers them.
 */

import BatEntity from '../entities/BatEntity.js';
import BatNestEntity, { NestState } from '../entities/BatNestEntity.js';
import { Events, TEX_WALL_CRUMBLED, TEX_BAT_WINGS_UP, TEX_BAT_WINGS_DOWN } from '../utils/constants.js';
import {
  BAT_NEST_MIN_LEVEL,
  BAT_NEST_SPAWN_CHANCE,
  BAT_NEST_ACTIVATION_RADIUS,
  BAT_NEST_BATS_COUNT,
  BAT_NEST_BAT_SPAWN_DELAY,
  BAT_NEST_MIN_SPAWN_DISTANCE,
  BAT_NEST_SIZE,
  BAT_NEST_MINI_BATS_COUNT,
  BAT_NEST_MINI_BAT_SIZE,
  BAT_NEST_FLAP_SPEED,
  BAT_NEST_ORBIT_RADIUS,
  BAT_NEST_FLY_OUT_CHANCE,
  BAT_NEST_FLY_OUT_DISTANCE,
  BAT_NEST_FLY_OUT_DURATION,
  BAT_NEST_IDLE_DRAIN_INTERVAL,
  BAT_NEST_IDLE_DRAIN_AMOUNT,
  BAT_NEST_ATTACK_COUNT,
  BAT_NEST_REATTACK_INTERVAL,
  BAT_REPEL_FLASH_DURATION,
  FOG_REVEAL_RADIUS,
  TILE_SIZE,
  AUDIO_BAT_NEST_ACTIVATE,
  AUDIO_BAT_HIT,
  AUDIO_BAT_REPEL,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

export default class BatNestSystem {
  /**
   * @param {Phaser.Scene} scene         - The GameScene instance.
   * @param {Phaser.Events.EventEmitter} eventBus - Shared event bus.
   * @param {number[][]} grid            - Maze grid (CellType values).
   * @param {object} playerRef           - Player sprite (has x, y, gridCol, gridRow).
   * @param {number[][]} visited         - Fog-of-war grid.
   * @param {number} level               - Current game level.
   * @param {{ col: number, row: number }} start - Player start position.
   * @param {{ col: number, row: number }} exit  - Maze exit position.
   * @param {object} healthSystem        - HealthSystem instance for applying bat damage.
   * @param {function(): boolean} hasTotemFn - Returns true if the player holds a Threia Totem.
   */
  constructor(scene, eventBus, grid, playerRef, visited, level, start, exit, healthSystem, hasTotemFn) {
    /** @type {Phaser.Scene} */
    this.scene = scene;
    /** @type {Phaser.Events.EventEmitter} */
    this._eventBus = eventBus;
    /** @type {number[][]} */
    this._grid = grid;
    /** @type {object} */
    this._playerRef = playerRef;
    /** @type {number[][]} */
    this._visited = visited;
    /** @type {number} */
    this._level = level;
    /** @type {{ col: number, row: number }} */
    this._start = start;
    /** @type {{ col: number, row: number }} */
    this._exit = exit;
    /** @type {object} HealthSystem for bat hit damage. */
    this._healthSystem = healthSystem;
    /** @type {function(): boolean} Returns true if the player has a Threia Totem. */
    this._hasTotemFn = hasTotemFn;

    /** @type {BatNestEntity[]} All nest entities (active and destroyed). */
    this._nests = [];

    /** @type {BatEntity[]} Bats spawned from nests (tracked for cleanup). */
    this._activeBats = [];

    /** @type {boolean} System paused flag. */
    this._paused = false;

    /** @type {Phaser.Time.TimerEvent[]} Delayed spawn timers. */
    this._spawnTimers = [];

    /**
     * Crumbled-wall sprites placed around each nest for visual effect.
     * Keyed by `${col},${row}` of the crumbled wall cell.
     * @type {Map<string, Phaser.GameObjects.Sprite>}
     */
    this._crumbledSprites = new Map();

    /**
     * Mini-bat sprites orbiting each nest, keyed by nest reference.
     * Each entry is an array of mini-bat data objects.
     * @type {Map<BatNestEntity, object[]>}
     */
    this._miniBats = new Map();

    /**
     * Accumulated idle drain timer (ms) for the player standing on a nest cell.
     * Resets to 0 when the player leaves the nest cell.
     * @type {number}
     */
    this._idleDrainTimer = 0;

    /**
     * The nest the player is currently standing on (for idle damage), or null.
     * @type {BatNestEntity|null}
     */
    this._playerOnNest = null;

    /**
     * Timer tracking ms since last re-attack check while player is near a nest.
     * Reset when player moves away from all nests' activation radius.
     * @type {number}
     */
    this._reattackTimer = 0;

    /**
     * Whether a nest bat attack is currently in progress (prevents overlapping attacks).
     * @type {boolean}
     */
    this._attackInProgress = false;

    // Only place nests if level is high enough
    if (this._level >= BAT_NEST_MIN_LEVEL) {
      this._placeNests();
    }
  }

  // ---------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------

  /**
   * Per-frame update. Handles:
   *   - Cooldown timers on nests
   *   - Fog visibility toggling for nests and crumbled walls
   *   - Active bat lifecycle (tick movement, hit detection, despawn out-of-range)
   *   - Re-attack logic when player lingers near active nests
   *   - Mini-bat sprite animation (orbit, wing flap, fly-out excursions)
   *   - Idle damage when player stands on a nest cell
   *
   * @param {number} _time
   * @param {number} delta
   */
  update(_time, delta) {
    if (this._paused || !this.scene || !this._playerRef) return;

    const playerCol = this._playerRef.gridCol;
    const playerRow = this._playerRef.gridRow;
    const now = this.scene.time.now;

    // --- Idle damage: check if player is standing on an active nest cell ---
    this._updateIdleDrain(playerCol, playerRow, delta);

    for (const nest of this._nests) {
      if (!nest.active) continue;

      // Update cooldown
      nest.updateCooldown(delta);

      // Fog visibility: nest is only visible within fog reveal radius
      const dc = Math.abs(nest.gridCol - playerCol);
      const dr = Math.abs(nest.gridRow - playerRow);
      const inFog = dc <= FOG_REVEAL_RADIUS && dr <= FOG_REVEAL_RADIUS;
      nest.setFogVisible(inFog);

      // Update crumbled wall visibility for this nest's adjacent cells
      if (nest._crumbledKeys) {
        for (const key of nest._crumbledKeys) {
          const spr = this._crumbledSprites.get(key);
          if (spr && spr.active) {
            const parts = key.split(',');
            const cc = parseInt(parts[0], 10);
            const cr = parseInt(parts[1], 10);
            const dcc = Math.abs(cc - playerCol);
            const dcr = Math.abs(cr - playerRow);
            const crumbInFog = dcc <= FOG_REVEAL_RADIUS && dcr <= FOG_REVEAL_RADIUS;
            spr.setAlpha(crumbInFog ? 0.85 : 0);
          }
        }
      }

      // Animate mini-bats for this nest
      this._updateMiniBats(nest, inFog, now, delta);
    }

    // --- Tick all active bats (movement + hit detection) ---
    for (let i = this._activeBats.length - 1; i >= 0; i--) {
      const bat = this._activeBats[i];
      if (!bat.active) {
        this._activeBats.splice(i, 1);
        continue;
      }

      const result = bat.tick(delta);

      // Handle hit result — bat successfully attacked the player
      if (result.hit) {
        this._onBatHit(bat, result.damage);
      }

      // Despawn bats that flew too far from the player
      if (bat.isActive) {
        const dx = bat.x - this._playerRef.x;
        const dy = bat.y - this._playerRef.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const maxDist = (FOG_REVEAL_RADIUS + 3) * TILE_SIZE;
        if (dist > maxDist) {
          bat.despawn();
        }
      }
    }

    // --- Re-attack logic: periodically send flying bats to attack while
    //     the player remains within activation radius of any active nest ---
    this._updateReattack(playerCol, playerRow, delta);
  }

  /**
   * Check proximity to nests and activate if within range.
   * Called from GameScene on PLAYER_MOVED events.
   *
   * @param {number} col - Player's current grid column.
   * @param {number} row - Player's current grid row.
   */
  checkProximity(col, row) {
    if (this._paused || this._level < BAT_NEST_MIN_LEVEL) return;

    for (const nest of this._nests) {
      if (!nest.active || !nest.canActivate) continue;

      // Manhattan distance in grid cells
      const dc = Math.abs(nest.gridCol - col);
      const dr = Math.abs(nest.gridRow - row);
      const dist = Math.max(dc, dr); // Chebyshev distance (tiles)

      if (dist <= BAT_NEST_ACTIVATION_RADIUS) {
        this._activateNest(nest);
      }
    }
  }

  /**
   * Handle a hammer hit on a cell. If a nest sits on this cell,
   * register the hit.
   *
   * @param {number} col - Grid column of the cell being hit.
   * @param {number} row - Grid row of the cell being hit.
   * @returns {boolean} True if a nest was hit (and system handled it).
   */
  onHammerHit(col, row) {
    for (const nest of this._nests) {
      if (!nest.active || nest.isDestroyed) continue;
      if (nest.gridCol === col && nest.gridRow === row) {
        const destroyed = nest.hammerHit();
        if (destroyed) {
          this._onNestDestroyed(nest);
        }
        return true;
      }
    }
    return false;
  }

  /**
   * Check if a cell has an active (non-destroyed) bat nest.
   * Used by GameScene to apply traversal penalty.
   *
   * @param {number} col
   * @param {number} row
   * @returns {boolean}
   */
  hasNestAt(col, row) {
    for (const nest of this._nests) {
      if (!nest.active || nest.isDestroyed) continue;
      if (nest.gridCol === col && nest.gridRow === row) return true;
    }
    return false;
  }

  /**
   * Get all nest positions (for minimap rendering etc.).
   * @returns {{ col: number, row: number, destroyed: boolean }[]}
   */
  getNestPositions() {
    return this._nests
      .filter(n => n.active)
      .map(n => ({
        col: n.gridCol,
        row: n.gridRow,
        destroyed: n.isDestroyed,
      }));
  }

  /**
   * Serialize all nest states for save.
   * @returns {object[]}
   */
  serialize() {
    return this._nests
      .filter(n => n.active)
      .map(n => n.serialize());
  }

  /**
   * Restore nests from saved data. Rebuilds nest entities and crumbled walls.
   * @param {object[]} data - Array of serialized nest data.
   */
  restore(data) {
    if (!data || !Array.isArray(data)) return;

    // Destroy current nests and crumbled sprites
    this._destroyAllVisuals();

    // Recreate from saved data
    for (const nd of data) {
      // Convert the wall cell to PATH (it was saved as a nest cell)
      if (nd.row >= 0 && nd.row < this._grid.length &&
          nd.col >= 0 && nd.col < this._grid[0].length) {
        this._grid[nd.row][nd.col] = 0; // CellType.PATH
      }

      // Remove the wall sprite that was rebuilt from the seed during maze construction
      this._removeWallSpriteAt(nd.col, nd.row);

      const nest = new BatNestEntity(this.scene, nd.col, nd.row);
      nest.restore(nd);
      this._nests.push(nest);

      // Rebuild crumbled wall visuals around the nest
      this._placeCrumbledWalls(nest);

      // Recreate animated mini-bat sprites
      this._createMiniBats(nest);
    }
  }

  /** Pause the system (stop activations and bat updates). */
  pause() {
    this._paused = true;

    // Pause spawn timers
    for (const timer of this._spawnTimers) {
      if (timer && !timer.hasDispatched) {
        timer.paused = true;
      }
    }
  }

  /** Resume the system. */
  resume() {
    this._paused = false;

    // Resume spawn timers
    for (const timer of this._spawnTimers) {
      if (timer && !timer.hasDispatched) {
        timer.paused = false;
      }
    }
  }

  /**
   * Destroy all nests, bats, crumbled walls, and clean up.
   */
  destroy() {
    // Cancel spawn timers
    for (const timer of this._spawnTimers) {
      if (timer) {
        timer.remove(false);
      }
    }
    this._spawnTimers = [];

    // Destroy all visual elements
    this._destroyAllVisuals();

    // Destroy bats spawned from nests
    for (const bat of this._activeBats) {
      if (bat.active) bat.destroy();
    }
    this._activeBats = [];

    this.scene = null;
    this._eventBus = null;
    this._grid = null;
    this._playerRef = null;
    this._visited = null;
    this._healthSystem = null;
    this._hasTotemFn = null;
  }

  // ---------------------------------------------------------------
  // Private — Mini-bat animation
  // ---------------------------------------------------------------

  /**
   * Create animated mini-bat sprites orbiting a nest. Each mini-bat is a tiny
   * sprite using TEX_BAT_WINGS_UP/DOWN that flutters around the nest center
   * and occasionally flies out beyond the nest.
   *
   * @private
   * @param {BatNestEntity} nest
   */
  _createMiniBats(nest) {
    if (!this.scene) return;

    const cx = nest.x;
    const cy = nest.y;
    const miniBats = [];

    for (let i = 0; i < BAT_NEST_MINI_BATS_COUNT; i++) {
      // Stagger initial orbit angle
      const baseAngle = (Math.PI * 2 * i) / BAT_NEST_MINI_BATS_COUNT + (Math.random() - 0.5) * 0.5;
      const orbitSpeed = 0.8 + Math.random() * 0.6; // radians per second
      const orbitRadius = BAT_NEST_ORBIT_RADIUS * (0.6 + Math.random() * 0.4);

      const spr = this.scene.add.sprite(cx, cy, TEX_BAT_WINGS_UP);
      spr.setDisplaySize(BAT_NEST_MINI_BAT_SIZE, BAT_NEST_MINI_BAT_SIZE);
      spr.setDepth(4); // Above nest (depth 3) and crumbled walls
      spr.setAlpha(0); // Hidden until fog reveals

      miniBats.push({
        sprite: spr,
        angle: baseAngle,
        orbitSpeed,
        orbitRadius,
        flapTimer: Math.random() * BAT_NEST_FLAP_SPEED,
        wingsUp: true,
        flyingOut: false,  // True when on an excursion away from nest
        flyOutX: 0,        // Target offset during fly-out
        flyOutY: 0,
        flyOutProgress: 0, // 0..1, used for ease-in-out round-trip
        flyOutDuration: 0,
      });
    }

    this._miniBats.set(nest, miniBats);
  }

  /**
   * Update all mini-bat sprites for a given nest: orbit flutter, wing flap,
   * and occasional fly-out excursions beyond the nest.
   *
   * @private
   * @param {BatNestEntity} nest
   * @param {boolean} inFog  - Whether the nest is currently visible (in fog radius).
   * @param {number} now     - Current scene time (ms).
   * @param {number} delta   - Frame delta (ms).
   */
  _updateMiniBats(nest, inFog, now, delta) {
    const bats = this._miniBats.get(nest);
    if (!bats || bats.length === 0) return;

    const cx = nest.x;
    const cy = nest.y;
    const isDestroyed = nest.isDestroyed;
    const deltaSec = delta / 1000;

    for (const mb of bats) {
      if (!mb.sprite || !mb.sprite.active) continue;

      // Destroyed nests: hide mini-bats
      if (isDestroyed) {
        mb.sprite.setAlpha(0);
        continue;
      }

      // Fog visibility
      mb.sprite.setAlpha(inFog ? 0.85 : 0);
      if (!inFog) continue;

      // Wing flap animation
      mb.flapTimer += delta;
      if (mb.flapTimer >= BAT_NEST_FLAP_SPEED) {
        mb.flapTimer -= BAT_NEST_FLAP_SPEED;
        mb.wingsUp = !mb.wingsUp;
        mb.sprite.setTexture(mb.wingsUp ? TEX_BAT_WINGS_UP : TEX_BAT_WINGS_DOWN);
      }

      // Fly-out excursion update
      if (mb.flyingOut) {
        mb.flyOutProgress += delta / mb.flyOutDuration;
        if (mb.flyOutProgress >= 1) {
          // Excursion complete — return to orbit
          mb.flyingOut = false;
          mb.flyOutProgress = 0;
        } else {
          // Smooth ease-in-out round trip: go out (0..0.5) then return (0.5..1)
          const t = mb.flyOutProgress;
          let factor;
          if (t < 0.5) {
            factor = 2 * t; // Going out: 0 → 1
          } else {
            factor = 2 * (1 - t); // Returning: 1 → 0
          }
          // Smooth easing
          factor = factor * factor * (3 - 2 * factor);

          mb.sprite.x = cx + mb.flyOutX * factor;
          mb.sprite.y = cy + mb.flyOutY * factor;
          continue; // Skip orbit update while flying out
        }
      }

      // Orbit movement (sinusoidal flutter)
      mb.angle += mb.orbitSpeed * deltaSec;
      const wobble = Math.sin(now * 0.003 + mb.angle * 2) * 2;
      mb.sprite.x = cx + Math.cos(mb.angle) * (mb.orbitRadius + wobble);
      mb.sprite.y = cy + Math.sin(mb.angle) * (mb.orbitRadius + wobble);

      // Random fly-out trigger (only if not already flying and nest is idle/cooldown)
      if (!mb.flyingOut && Math.random() < BAT_NEST_FLY_OUT_CHANCE) {
        mb.flyingOut = true;
        mb.flyOutProgress = 0;
        mb.flyOutDuration = BAT_NEST_FLY_OUT_DURATION * (0.7 + Math.random() * 0.6);
        // Random direction outward from nest, beyond maze walls
        const outAngle = Math.random() * Math.PI * 2;
        const outDist = BAT_NEST_FLY_OUT_DISTANCE * (0.5 + Math.random() * 0.5);
        mb.flyOutX = Math.cos(outAngle) * outDist;
        mb.flyOutY = Math.sin(outAngle) * outDist;
      }
    }
  }

  /**
   * Destroy mini-bat sprites for a specific nest.
   * @private
   * @param {BatNestEntity} nest
   */
  _destroyMiniBatsForNest(nest) {
    const bats = this._miniBats.get(nest);
    if (!bats) return;
    for (const mb of bats) {
      if (mb.sprite && mb.sprite.active) mb.sprite.destroy();
    }
    this._miniBats.delete(nest);
  }

  // ---------------------------------------------------------------
  // Private — Idle drain on nest cell
  // ---------------------------------------------------------------

  /**
   * Track whether the player is standing on an active nest cell and apply
   * periodic damage while they remain there.
   *
   * @private
   * @param {number} playerCol
   * @param {number} playerRow
   * @param {number} delta - Frame delta (ms).
   */
  _updateIdleDrain(playerCol, playerRow, delta) {
    // Find if player is on an active (non-destroyed) nest
    let onNest = null;
    for (const nest of this._nests) {
      if (!nest.active || nest.isDestroyed) continue;
      if (nest.gridCol === playerCol && nest.gridRow === playerRow) {
        onNest = nest;
        break;
      }
    }

    if (onNest) {
      if (this._playerOnNest !== onNest) {
        // Just stepped onto this nest — reset timer
        // (The initial traversal damage is handled by GameScene on PLAYER_MOVED)
        this._playerOnNest = onNest;
        this._idleDrainTimer = 0;
      }

      // Accumulate idle time
      this._idleDrainTimer += delta;
      if (this._idleDrainTimer >= BAT_NEST_IDLE_DRAIN_INTERVAL) {
        this._idleDrainTimer -= BAT_NEST_IDLE_DRAIN_INTERVAL;

        // Apply idle damage via healthSystem (accessed through scene)
        if (this.scene && this.scene.healthSystem) {
          this.scene.healthSystem.takeDamage(BAT_NEST_IDLE_DRAIN_AMOUNT);
        }
      }
    } else {
      // Player not on any nest — reset
      this._playerOnNest = null;
      this._idleDrainTimer = 0;
    }
  }

  // ---------------------------------------------------------------
  // Private — Placement
  // ---------------------------------------------------------------

  /**
   * Place bat nests on interior bridge-wall cells during level generation.
   * Picks 1-2 nests based on level scaling and spawn chance.
   *
   * Bridge walls are wall cells (even indices) that sit between two path cells,
   * either horizontally or vertically. The nest replaces the wall, converting
   * the cell to walkable (with penalty), and adds crumbled-wall visuals on
   * adjacent walls.
   * @private
   */
  _placeNests() {
    // Roll spawn chance
    if (Math.random() >= BAT_NEST_SPAWN_CHANCE) return;

    // Determine nest count: 1 at levels 4-7, 1-2 at levels 8+
    let nestCount = 1;
    if (this._level >= 8 && Math.random() < 0.40) {
      nestCount = 2;
    }

    // Collect candidate bridge-wall cells
    const candidates = this._findCandidateWalls();
    if (candidates.length === 0) return;

    // Shuffle candidates
    this._shuffleArray(candidates);

    const placed = [];
    for (const cand of candidates) {
      if (placed.length >= nestCount) break;

      // Ensure minimum distance from player start
      const distFromStart = Math.abs(cand.col - this._start.col) +
                            Math.abs(cand.row - this._start.row);
      if (distFromStart < BAT_NEST_MIN_SPAWN_DISTANCE) continue;

      // Ensure minimum distance from exit
      const distFromExit = Math.abs(cand.col - this._exit.col) +
                           Math.abs(cand.row - this._exit.row);
      if (distFromExit < 3) continue;

      // Ensure minimum distance from other placed nests (at least 6 tiles apart)
      let tooClose = false;
      for (const p of placed) {
        const d = Math.abs(cand.col - p.col) + Math.abs(cand.row - p.row);
        if (d < 6) {
          tooClose = true;
          break;
        }
      }
      if (tooClose) continue;

      // Convert the wall cell to PATH so the player can walk through (with penalty)
      this._grid[cand.row][cand.col] = 0; // CellType.PATH

      // Remove the original wall sprite at this cell (if the scene has a wallGroup)
      this._removeWallSpriteAt(cand.col, cand.row);

      // Place the nest entity on this cell
      const nest = new BatNestEntity(this.scene, cand.col, cand.row);
      this._nests.push(nest);
      placed.push(cand);

      // Add crumbled wall visuals on adjacent wall cells
      this._placeCrumbledWalls(nest);

      // Create animated mini-bat sprites orbiting the nest
      this._createMiniBats(nest);
    }
  }

  /**
   * Find interior wall cells that bridge two path cells (horizontal or vertical).
   * A bridge wall sits at an even index between two odd-index path cells.
   *
   * @private
   * @returns {{ col: number, row: number }[]}
   */
  _findCandidateWalls() {
    const gridH = this._grid.length;
    const gridW = this._grid[0].length;
    const walls = [];

    for (let r = 2; r < gridH - 2; r++) {
      for (let c = 2; c < gridW - 2; c++) {
        // Must be a wall cell (value 1)
        if (this._grid[r][c] !== 1) continue;

        // Check horizontal bridge: path - wall - path
        if (r % 2 === 1 && c % 2 === 0) {
          if (this._grid[r][c - 1] === 0 && this._grid[r][c + 1] === 0) {
            walls.push({ col: c, row: r });
          }
        }
        // Check vertical bridge: path - wall - path
        if (r % 2 === 0 && c % 2 === 1) {
          if (this._grid[r - 1][c] === 0 && this._grid[r + 1][c] === 0) {
            walls.push({ col: c, row: r });
          }
        }
      }
    }

    return walls;
  }

  /**
   * Remove the wall sprite from the scene's wallGroup at a given grid cell.
   * Scans the wallGroup children for the sprite whose world position matches.
   * @private
   * @param {number} col
   * @param {number} row
   */
  _removeWallSpriteAt(col, row) {
    if (!this.scene || !this.scene.wallGroup) return;

    const wx = col * TILE_SIZE + TILE_SIZE / 2;
    const wy = row * TILE_SIZE + TILE_SIZE / 2;
    const tolerance = TILE_SIZE * 0.4;

    const children = this.scene.wallGroup.getChildren();
    for (let i = children.length - 1; i >= 0; i--) {
      const child = children[i];
      if (Math.abs(child.x - wx) < tolerance && Math.abs(child.y - wy) < tolerance) {
        child.destroy();
        break;
      }
    }
  }

  /**
   * Place crumbled-wall overlay sprites on wall cells adjacent to a nest.
   * These give the visual impression that the nest has partially destroyed
   * the surrounding walls.
   *
   * Adjacent wall cells are checked in the 4 cardinal directions from the
   * nest cell. Only cells that are actually walls (value 1) get overlays.
   * @private
   * @param {BatNestEntity} nest
   */
  _placeCrumbledWalls(nest) {
    if (!this.scene) return;

    const col = nest.gridCol;
    const row = nest.gridRow;
    const dirs = [
      { dc: -1, dr: 0 },
      { dc: 1, dr: 0 },
      { dc: 0, dr: -1 },
      { dc: 0, dr: 1 },
    ];

    // Track which crumbled sprite keys belong to this nest
    nest._crumbledKeys = [];

    for (const d of dirs) {
      const nc = col + d.dc;
      const nr = row + d.dr;

      // Check bounds
      if (nr < 0 || nr >= this._grid.length || nc < 0 || nc >= this._grid[0].length) continue;

      // Only overlay on actual wall cells
      if (this._grid[nr][nc] !== 1) continue;

      const key = `${nc},${nr}`;
      // Don't duplicate if another nest already placed a crumbled sprite here
      if (this._crumbledSprites.has(key)) {
        nest._crumbledKeys.push(key);
        continue;
      }

      const wx = nc * TILE_SIZE + TILE_SIZE / 2;
      const wy = nr * TILE_SIZE + TILE_SIZE / 2;

      const spr = this.scene.add.sprite(wx, wy, TEX_WALL_CRUMBLED);
      spr.setDisplaySize(TILE_SIZE, TILE_SIZE);
      spr.setDepth(3); // Above walls (depth 2), visible as overlay
      spr.setAlpha(0); // Hidden until fog reveals

      this._crumbledSprites.set(key, spr);
      nest._crumbledKeys.push(key);
    }
  }

  /**
   * Fisher-Yates shuffle (in place).
   * @private
   * @param {any[]} arr
   */
  _shuffleArray(arr) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }

  // ---------------------------------------------------------------
  // Private — Activation
  // ---------------------------------------------------------------

  /**
   * Activate a nest — play sound, spawn bats that visibly emerge from the
   * nest center. A subset of bats (BAT_NEST_ATTACK_COUNT) will immediately
   * dive-attack the player after emerging; the rest fly erratically.
   * @private
   * @param {BatNestEntity} nest
   */
  _activateNest(nest) {
    nest.activate();

    // Play activation sound (swarm/screech)
    try {
      this.scene.sound.play(AUDIO_BAT_NEST_ACTIVATE, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio may be missing */ }

    // Emit event
    this._eventBus.emit(Events.BAT_NEST_ACTIVATED, {
      col: nest.gridCol,
      row: nest.gridRow,
    });

    // Nest center in world coordinates
    const nestWorldX = nest.gridCol * TILE_SIZE + TILE_SIZE / 2;
    const nestWorldY = nest.gridRow * TILE_SIZE + TILE_SIZE / 2;

    // Determine which bats will attack (first N spawned)
    const attackCount = Math.min(BAT_NEST_ATTACK_COUNT, BAT_NEST_BATS_COUNT);

    // Pre-compute staggered emergence angles (spread evenly around the nest)
    const baseAngle = Math.random() * Math.PI * 2;
    const angles = [];
    for (let i = 0; i < BAT_NEST_BATS_COUNT; i++) {
      angles.push(baseAngle + (Math.PI * 2 * i) / BAT_NEST_BATS_COUNT + (Math.random() - 0.5) * 0.5);
    }

    for (let i = 0; i < BAT_NEST_BATS_COUNT; i++) {
      const delay = i * BAT_NEST_BAT_SPAWN_DELAY;
      const willAttack = i < attackCount;
      const angle = angles[i];

      const timer = this.scene.time.delayedCall(delay, () => {
        if (!this.scene || !this._playerRef || this._paused) return;

        // Spawn bat exactly at the nest center
        const bat = new BatEntity(
          this.scene,
          nestWorldX,
          nestWorldY,
          this._playerRef
        );

        // Start emergence: bat flies outward from nest before normal AI
        bat.startEmerging(nestWorldX, nestWorldY, angle, willAttack);

        this._activeBats.push(bat);
        this._eventBus.emit(Events.BAT_SPAWNED);

        // Remove timer from tracked list
        const idx = this._spawnTimers.indexOf(timer);
        if (idx !== -1) this._spawnTimers.splice(idx, 1);
      });

      this._spawnTimers.push(timer);
    }

    // After all bats spawned, put nest in cooldown
    const cooldownDelay = BAT_NEST_BATS_COUNT * BAT_NEST_BAT_SPAWN_DELAY + 100;
    const cooldownTimer = this.scene.time.delayedCall(cooldownDelay, () => {
      if (nest.active && nest.nestState === NestState.ACTIVE) {
        nest.startCooldown();
      }
      const idx = this._spawnTimers.indexOf(cooldownTimer);
      if (idx !== -1) this._spawnTimers.splice(idx, 1);
    });
    this._spawnTimers.push(cooldownTimer);
  }

  // ---------------------------------------------------------------
  // Private — Bat hit / repel / re-attack
  // ---------------------------------------------------------------

  /**
   * Handle a nest bat successfully hitting the player — apply damage,
   * emit events, and show a red flash on the player sprite.
   * @private
   * @param {BatEntity} bat
   * @param {number} damage
   */
  _onBatHit(bat, damage) {
    this._attackInProgress = false;

    if (this._healthSystem) {
      this._healthSystem.takeDamage(damage);
    }

    this._eventBus.emit(Events.BAT_HIT, damage);

    // Red flash on player to indicate damage
    if (this._playerRef && this._playerRef.active) {
      this._playerRef.setTint(0xff4444);
      if (this.scene) {
        this.scene.time.delayedCall(250, () => {
          if (this._playerRef && this._playerRef.active) {
            this._playerRef.clearTint();
          }
        });
      }
    }
  }

  /**
   * Repel a bat using the Threia Totem — blinding flash, bat flees, no damage.
   * @private
   * @param {BatEntity} bat
   */
  _repelBat(bat) {
    this._attackInProgress = false;
    this._eventBus.emit(Events.BAT_REPELLED);

    // Play repel sound
    try {
      this.scene.sound.play(AUDIO_BAT_REPEL, { volume: getSfxVol(0.5) });
    } catch (_) { /* audio may be missing */ }

    // Blinding circular flash of white light from the player
    if (this.scene && this._playerRef) {
      const px = this._playerRef.x;
      const py = this._playerRef.y;
      const radius = TILE_SIZE * 3;
      const alphaObj = { val: 0.9, scale: 1.0 };

      const flash = this.scene.add.graphics();
      flash.setDepth(250);

      const drawFlash = () => {
        flash.clear();
        const r = radius * alphaObj.scale;
        flash.fillStyle(0xffffff, alphaObj.val * 0.3);
        flash.fillCircle(px, py, r);
        flash.fillStyle(0xffffff, alphaObj.val * 0.6);
        flash.fillCircle(px, py, r * 0.6);
        flash.fillStyle(0xffffff, alphaObj.val);
        flash.fillCircle(px, py, r * 0.3);
      };

      drawFlash();

      this.scene.tweens.add({
        targets: alphaObj,
        val: 0,
        scale: 1.5,
        duration: BAT_REPEL_FLASH_DURATION,
        ease: 'Quad.easeOut',
        onUpdate: drawFlash,
        onComplete: () => {
          flash.destroy();
        },
      });
    }

    // Bat flees
    bat.repel();
  }

  /**
   * Periodically check if the player is still near an active nest, and if so,
   * send one of the currently flying bats to attack. This creates sustained
   * aggression as long as the player lingers near a nest.
   *
   * If the player holds a Threia Totem, the attacking bat is repelled instead.
   *
   * @private
   * @param {number} playerCol
   * @param {number} playerRow
   * @param {number} delta
   */
  _updateReattack(playerCol, playerRow, delta) {
    // Check if the player is within activation radius of any non-destroyed nest
    let nearNest = false;
    for (const nest of this._nests) {
      if (!nest.active || nest.isDestroyed) continue;
      const dc = Math.abs(nest.gridCol - playerCol);
      const dr = Math.abs(nest.gridRow - playerRow);
      if (Math.max(dc, dr) <= BAT_NEST_ACTIVATION_RADIUS) {
        nearNest = true;
        break;
      }
    }

    if (!nearNest) {
      this._reattackTimer = 0;
      return;
    }

    // Accumulate re-attack timer
    this._reattackTimer += delta;
    if (this._reattackTimer < BAT_NEST_REATTACK_INTERVAL) return;
    this._reattackTimer -= BAT_NEST_REATTACK_INTERVAL;

    // Don't start a new attack if one is already in progress
    if (this._attackInProgress) return;

    // Find the nearest flying bat to send as attacker
    let nearest = null;
    let nearestDist = Infinity;

    for (const bat of this._activeBats) {
      if (!bat.active || bat.batState !== 'flying') continue;
      const dx = bat.x - this._playerRef.x;
      const dy = bat.y - this._playerRef.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = bat;
      }
    }

    if (!nearest) return;

    this._attackInProgress = true;

    // Check Threia Totem protection
    if (this._hasTotemFn && this._hasTotemFn()) {
      this._repelBat(nearest);
    } else {
      nearest.startAttack();
      this._eventBus.emit(Events.BAT_ATTACK);
    }
  }

  // ---------------------------------------------------------------
  // Private — Destruction
  // ---------------------------------------------------------------

  /**
   * Handle nest destruction by hammer — emit event and clean up
   * crumbled wall overlays around the destroyed nest.
   * @private
   * @param {BatNestEntity} nest
   */
  _onNestDestroyed(nest) {
    this._eventBus.emit(Events.BAT_NEST_DESTROYED, {
      col: nest.gridCol,
      row: nest.gridRow,
    });

    // Destroy mini-bat sprites for this nest
    this._destroyMiniBatsForNest(nest);

    // Remove crumbled wall overlays for this nest (unless shared by another nest)
    if (nest._crumbledKeys) {
      for (const key of nest._crumbledKeys) {
        // Check if any other active non-destroyed nest also uses this key
        let sharedByOther = false;
        for (const other of this._nests) {
          if (other === nest || !other.active || other.isDestroyed) continue;
          if (other._crumbledKeys && other._crumbledKeys.includes(key)) {
            sharedByOther = true;
            break;
          }
        }

        if (!sharedByOther) {
          const spr = this._crumbledSprites.get(key);
          if (spr && spr.active) {
            // Fade out and destroy
            if (this.scene) {
              this.scene.tweens.add({
                targets: spr,
                alpha: 0,
                duration: 400,
                ease: 'Quad.easeOut',
                onComplete: () => {
                  spr.destroy();
                },
              });
            } else {
              spr.destroy();
            }
          }
          this._crumbledSprites.delete(key);
        }
      }
      nest._crumbledKeys = [];
    }
  }

  // ---------------------------------------------------------------
  // Private — Cleanup
  // ---------------------------------------------------------------

  /**
   * Destroy all nest entities and crumbled wall sprites.
   * @private
   */
  _destroyAllVisuals() {
    // Destroy mini-bat sprites for all nests
    for (const nest of this._nests) {
      this._destroyMiniBatsForNest(nest);
    }
    this._miniBats.clear();

    // Destroy nests
    for (const nest of this._nests) {
      if (nest.active) nest.destroy();
    }
    this._nests = [];

    // Destroy crumbled wall sprites
    for (const spr of this._crumbledSprites.values()) {
      if (spr && spr.active) spr.destroy();
    }
    this._crumbledSprites.clear();
  }
}
